import type {
  ErutAnalysisResponse,
  FacialVerificationResponse,
  OcrChileResponse,
  PepChileCheckResult,
} from 'src/app/auth/services/kyc-api.service';
import type { CheckDocumentResponse } from 'src/app/auth/services/person-registration.service';

/**
 * Datos de mentira para recorrer el registro de empresas sin backend.
 *
 * Solo los usa `SignupPreviewService`, que esta apagado en cualquier build de
 * produccion. Nada de esto se envia a ninguna parte: los servicios cortan antes
 * de la llamada de red.
 *
 * Los tipos vienen de los servicios reales a proposito. Si el contrato del KYC
 * cambia, estos objetos dejan de compilar y el preview se entera antes que QA.
 */

/**
 * RUT del representante legal. Tiene que ser el mismo en el OCR y en el
 * `rutUsuario` del e-RUT: el paso 10 cruza los dos y bloquea si no coinciden,
 * asi que si se los deja distintos el preview no pasa de ahi.
 */
export const PREVIEW_RUT_REPRESENTANTE = '11.111.111-1';
export const PREVIEW_RUT_REPRESENTANTE_LIMPIO = '111111111';

/** RUT de la empresa, el que el e-RUT declara como titular. */
export const PREVIEW_RUT_EMPRESA = '77.777.777-7';

/**
 * Imagen de relleno para los recuadros de documento y selfie. Es un SVG rotulado
 * y no una foto: al verlo en pantalla queda claro que el paso no se recorrio.
 */
function placeholder(rotulo: string, fondo: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="480" height="300">
    <rect width="480" height="300" fill="${fondo}"/>
    <text x="240" y="150" font-family="sans-serif" font-size="26" font-weight="600"
          fill="#ffffff" text-anchor="middle">${rotulo}</text>
    <text x="240" y="182" font-family="sans-serif" font-size="15"
          fill="#ffffff" opacity="0.75" text-anchor="middle">imagen de prueba</text>
  </svg>`;
  return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
}

export const PREVIEW_IMG_DOC_FRENTE = placeholder('Documento — frente', '#154e3a');
export const PREVIEW_IMG_DOC_REVERSO = placeholder('Documento — reverso', '#0e7705');
export const PREVIEW_IMG_SELFIE = placeholder('Selfie', '#082c22');

/** Respuesta cruda del OCR chileno, con los nombres de campo que manda el KYC. */
export const PREVIEW_OCR_CHILE: OcrChileResponse = {
  tipoDocumento: 'Cedula de Identidad',
  pais: 'CHL',
  campos: {
    nombres: 'MARIA JOSE',
    apellidos: 'SOTO RIVERA',
    run: PREVIEW_RUT_REPRESENTANTE,
    runSinFormato: PREVIEW_RUT_REPRESENTANTE_LIMPIO,
    nacionalidad: 'CHILENA',
    sexo: 'F',
    fechaNacimiento: '14 MAR 1988',
    fechaNacimientoISO: '1988-03-14',
    fechaVencimiento: '20 SEP 2031',
    fechaVencimientoISO: '2031-09-20',
    fechaEmision: '20 SEP 2021',
    fechaEmisionISO: '2021-09-20',
  },
  validacion: {
    runValido: true,
    estaVencido: false,
    confianzaOCR: 98,
    camposCompletos: true,
    diasHastaVencimiento: 1860,
    advertencias: [],
  },
  fotoPersona: PREVIEW_IMG_DOC_FRENTE,
  verificacionSanciones: {
    hasMatches: false,
    riskLevel: 'LOW',
    matches: [],
  },
  metadata: {
    processingTimeMs: 0,
    servicesUsed: ['preview'],
    imageQuality: 'GOOD',
  },
};

/** Documento libre: en preview nunca se choca con un registro previo. */
export const PREVIEW_CHECK_DOCUMENT: CheckDocumentResponse = {
  isRegistered: false,
  message: 'Documento disponible (preview)',
};

/** Coincidencia por encima del umbral y liveness sin observaciones. */
export const PREVIEW_FACIAL: FacialVerificationResponse = {
  isMatch: true,
  similarity: 96.4,
  threshold: 80,
  sourceFaceDetected: true,
  targetFaceDetected: true,
  calidadSelfie: {
    suficiente: true,
    motivo: null,
    mensaje: null,
  },
  antiSpoof: {
    livenessScore: 97,
    recommendation: 'accept',
    rejectReason: null,
    blocked: false,
    riskScore: 3,
    reasons: [],
  },
  metadata: { processingTimeMs: 0 },
};

/** e-RUT aceptado, con el `rutUsuario` alineado al OCR para que el cruce pase. */
export const PREVIEW_ERUT: ErutAnalysisResponse = {
  recommendation: 'accept',
  integrityScore: 100,
  checks: [],
  reasons: [],
  notes: ['Analisis simulado: el preview no manda el PDF a ninguna parte.'],
  fields: {
    rut: PREVIEW_RUT_EMPRESA,
    razonSocial: 'COMERCIALIZADORA LOS ALERCES SPA',
    direccion: 'AV. APOQUINDO 4501, OF. 1203, LAS CONDES, SANTIAGO',
    serie: 'A-000123',
    glosaActividad: 'VENTA AL POR MAYOR DE ALIMENTOS Y BEBIDAS',
    fechaEmision: '2026-07-30',
    rutUsuario: PREVIEW_RUT_REPRESENTANTE,
    usuario: 'MARIA JOSE SOTO RIVERA',
  },
  crossCheck: {
    allMatch: true,
    mismatchedFields: [],
    unprotectedFields: ['glosaActividad', 'fechaEmision', 'rutUsuario', 'usuario'],
  },
  rutCheckDigitValid: true,
  sha256: '0'.repeat(64),
  verifiedWithSii: false,
};

/**
 * Sin coincidencia en la lista PEP: el paso del PEP arranca con la declaracion en
 * blanco, que es el camino que mas interesa revisar.
 */
export const PREVIEW_PEP_CHILE: PepChileCheckResult = {
  isPep: false,
  isFuncionarioPublico: false,
  pepLevel: 0,
  listsChecked: [],
  dni: PREVIEW_RUT_REPRESENTANTE_LIMPIO,
  fullName: 'MARIA JOSE SOTO RIVERA',
  metadata: {
    timestamp: '1970-01-01T00:00:00.000Z',
    checkedDni: PREVIEW_RUT_REPRESENTANTE_LIMPIO,
    processingTimeMs: 0,
  },
};

/** Usuario falso, para los pasos que exigen `usuarioId` para guardar. */
export const PREVIEW_USUARIO_ID = 999999;
export const PREVIEW_EMAIL = 'preview.empresa@aguacatewallet.com';
